import { ChangeLog, Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import {
  TransactionMonitoringRule,
  TransactionMonitoringRulesValidators,
  TransactionMonitoringRuleFormValues,
  Organization,
} from '../../types';

export interface TransactionMonitoringRulesValidatorsResponse {
  data: TransactionMonitoringRulesValidators;
}

export const transactionMonitoringRulesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTransactionMonitoringRulesValidators: build.query<
      TransactionMonitoringRulesValidatorsResponse,
      void
    >({
      query: () => ({
        url: '/v1/monitorings-rules/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMonitoringRulesOrganization: build.query<Response<Organization>, string>(
      {
        query: (query) => ({
          url: `/v1/clients-organizations?take=30&status=ACTIVE${query}`,
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getMonitoringRulesOrganizationsByIds: build.query<
      Response<Organization>,
      string
    >({
      query: (query) => ({
        url: `/v1/clients-organizations?all=true${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionMonitoringRules: build.query<
      Response<TransactionMonitoringRule>,
      string
    >({
      query: (query) => ({
        url: `/v1/monitorings-rules${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createTransactionMonitoringRule: build.mutation<
      {
        data: TransactionMonitoringRule;
      },
      TransactionMonitoringRuleFormValues
    >({
      query: (body: TransactionMonitoringRuleFormValues) => ({
        url: '/v1/monitorings-rules',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTransactionMonitoringRule: build.mutation<
      {
        data: TransactionMonitoringRule;
      },
      { body: TransactionMonitoringRuleFormValues; id: string }
    >({
      query: (data: {
        body: TransactionMonitoringRuleFormValues;
        id: string;
      }) => ({
        url: `/v1/monitorings-rules/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    toggleTransationMonitoringRulesActive: build.mutation<
      {
        data: TransactionMonitoringRule;
      },
      { active: boolean; id: string }
    >({
      query: (data: { active: boolean; id: string }) => ({
        url: `/v1/monitorings-rules/${data.id}/active`,
        method: 'PUT',
        body: {
          active: data.active,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchToggleTransationMonitoringRulesActive: build.mutation<
      {
        data: TransactionMonitoringRule;
      },
      { active: boolean; ids: string[] }
    >({
      query: (body) => ({
        url: '/v1/monitorings-rules/batch-update-active',
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionMonitoringRule: build.query<
      {
        data: TransactionMonitoringRule;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/monitorings-rules/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMonitoringRuleChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetTransactionMonitoringRulesValidatorsQuery,
  useGetTransactionMonitoringRulesQuery,
  useLazyGetTransactionMonitoringRulesQuery,
  useCreateTransactionMonitoringRuleMutation,
  useUpdateTransactionMonitoringRuleMutation,
  useLazyGetTransactionMonitoringRuleQuery,
  useToggleTransationMonitoringRulesActiveMutation,
  useBatchToggleTransationMonitoringRulesActiveMutation,
  useLazyGetMonitoringRuleChangeLogsQuery,
  useLazyGetMonitoringRulesOrganizationQuery,
  useLazyGetMonitoringRulesOrganizationsByIdsQuery,
} = transactionMonitoringRulesApi;

export const {
  endpoints: {
    getTransactionMonitoringRulesValidators,
    getTransactionMonitoringRules,
    createTransactionMonitoringRule,
    updateTransactionMonitoringRule,
    toggleTransationMonitoringRulesActive,
    getTransactionMonitoringRule,
    batchToggleTransationMonitoringRulesActive,
    getMonitoringRuleChangeLogs,
    getMonitoringRulesOrganization,
    getMonitoringRulesOrganizationsByIds,
  },
} = transactionMonitoringRulesApi;
